'use client';
import { Button } from '@usitsdasdesign/dds-react';
import Heading from 'atoms/Heading';
import Loader from 'atoms/Loader';
import Text from 'atoms/Text';
import { projectDetailAllocationSortItems, projectDetailAllocationTabFiltersItems, projectDetailSearchFilterItems } from 'helper/constants';
import { allocationToPractitioner } from 'helper/practitioners';
import { extractApiErrorMessage, findFirstStringByKey } from 'helper/util';
import useDemandHooks from 'hooks/useDemandHooks';
import { getFiltersString, getSearchString, getSortString, throttle } from 'lib/util';
import AlertModal from 'molecules/AlertModal';
import DropdownV2 from 'molecules/Dropdown';
import MultiChangeSchedule from 'molecules/MultiChangeSchedule';
import ResourceProfile from 'molecules/ResourceProfile';
import Schedule from 'molecules/Schedule';
import Table from 'molecules/Table';
import Toast from 'molecules/Toast';
import TruncatedTextWithTooltip from 'molecules/TruncatedTextWithTooltip';
import moment from 'moment';
import { useSession } from 'next-auth/react';
import ActionsWithFilter from 'organisms/ActionsWithFilter';
import EmployeeScheduleConflict from 'organisms/EmployeeScheduleConflict';
import UnAllocate from 'organisms/UnAllocate';
import { useEffect, useState } from 'react';
import { bulkUnAllocation, editTenure, getAllAllocationsFilter, getAllocationUnderProjectCSV, updateAllocation } from 'services/allocation.service';
import { trackAdobeAnalyticEvent } from 'services/analytics-tool.service';
import { createConflict } from 'services/conflict.service';
import * as FilterService from 'services/filter.service';
import { updateBulkSchedule } from 'services/schedule.service';
import useGlobalToastStore from 'store/globalToastStore';

const limit = 10;

export const RenderText = ({
    title, subTitle, className = '', hideSubtitle = false
}) => (
    <>
        <Text className={`font-semibold ${className}`}>{title}</Text>
        {!hideSubtitle && <Text variant='label' color='text-gray-700'>({subTitle})</Text>}
    </>
);

const customMessage = <>
    <Text variant='body'>
        The file is being generated and may take a few minutes to complete. Would you like to proceed?
    </Text>
    <Text variant='body' className='mt-4'>
        Note: Please do not refresh or leave the page. The download will start automatically once the file is ready.
    </Text>
</>

const PractitionerAllocatedView = ({ projectId, fetchWalletBalance, resourceType = 'employees', }) => {
    const session = useSession();
    const { onHavingConflictShowAlert, onHavingBudgetExceededShowAlert } = useDemandHooks();
    const [isArticle, setIsArticle] = useState(false);
    const [rows, setRows] = useState([]);
    const [total, setTotal] = useState(0);
    const [selectedRowData, setSelectedRowData] = useState({});
    const [showLoader, setShowLoader] = useState(true);
    const [showDialog, setShowDialog] = useState('');
    const [showToast, setShowToast] = useState({ visible: false, message: '', variant: 'success' });
    const { setToastData } = useGlobalToastStore();
    const [paginationOption, setPaginationOption] = useState({
        currentPage: 1,
        pageNumberInSection: limit,
        pageLength: 1
    });
    const [search, setSearch] = useState('');
    const [filterAndSort, setFilterAndSort] = useState({ filters: {} });
    const [showConflict, setShowConflict] = useState({ show: false, noOfError: 0, data: {} });
    const [alertModalData, setAlertModalData] = useState('');
    const [formData, setFormData] = useState({});
    const isAdminOrRM = ['admin', 'resource_manager', 'guest_rm', 'resource_manager_lead'].some((role) => session.data.user.role.includes(role));
    const isManagerOrPartner = ['manager', 'partner'].includes(session.data.user.gradeLevel) && !isAdminOrRM;
    
    const [selectMultiRows, setSelectMultiRows] = useState([]);
    const [unAllocateData, setUnAllocateData] = useState(null);

    const columns = [
        {
            Header: 'Practitioners Details',
            accessor: (row) => (
                <>
                    <ResourceProfile
                        name={row.practitioner.name}
                        id={row.practitioner.ID}
                        profileImage={row?.practitioner?.profileImage}
                        slugId={row.practitioner.resId}
                        isContinuityResource={row?.isContinuityResource}
                        isNameFullWidth={true}
                        customBadge={row?.hasPendingUnallocationRequest ? { label: 'Unallocation request in progress', color: 'green' } : null}
                    />
                </>
            ),
            tdClass: 'min-w-[220px]'
        },
        { Header: 'Email ID', accessor: 'emailID' },
        {
            Header: 'Demand Details', accessor: (row) => (
                <>
                    {/* <Link href={`/projects/${projectId}/demands/${row.demandId}`} legacyBehavior>
                    <a className="text-green-900" target="_blank" rel="noopener noreferrer">
                        {row.demandName}
                    </a>
                </Link> */}
                    <Text>{row.demandName}</Text>
                    <Text>
                        ID: {row?.demandCode}
                    </Text>
                    {row?.isPodWorkpackage && (
                        <div className="bg-purple-600 text-white text-xs px-2.5 py-0.5 w-fit mt-1">
                            CEC 2.0
                        </div>
                    )}
                </>
            ),
            cellwidth: 154
        },
        { Header: 'Shift Start Date', accessor: 'shiftStartDate'},
        { Header: 'Shift End Date', accessor: 'shiftEndDate'},
        { Header: 'Shift Category', accessor: 'shiftCategory'},
        { Header: 'Shift Timings', accessor: 'shiftTiming'},
        { Header: 'Designation', 
            accessor: (row) => (                
                <TruncatedTextWithTooltip lengthThreshold={18} maxWidth = "max-w-[154px]" text={row.designation} />
            ),
            cellwidth: 154
        },
        {
            Header: 'Function', accessor: (row) => (
                <TruncatedTextWithTooltip lengthThreshold={18} maxWidth="max-w-[154px]" text={row.function} />
            ),
            cellwidth: 154
        },
        {
            Header: 'Service Area', accessor: (row) => (
                <TruncatedTextWithTooltip lengthThreshold={18} maxWidth="max-w-[154px]" text={row.serviceArea} />
            ),
            cellwidth: 154
        },
        {
            Header: 'Sub-service area', accessor: (row) => (
                <TruncatedTextWithTooltip lengthThreshold={18} maxWidth="max-w-[154px]" text={row.subServiceArea} />
            ),
            cellwidth: 154
        },
        {
            Header: 'Location', accessor: (row) => (
                <Text>{row.practitioner.location}</Text>
            ),
            cellwidth: 154
        },
        { Header: 'Allocation Start Date', accessor: 'allocationStartDate', cellwidth: 82 },
        { Header: 'Allocation End Date', accessor: 'allocationEndDate', cellwidth: 82 },
        { Header: 'Hours per week ', accessor: 'hoursPerWeek', cellwidth: 82 },
        {
            Header: 'Created by',
            accessor: (row) => (
                row.createdBy?.name ? (<RenderText
                    title={row.createdBy.name}
                    subTitle={row.createdBy.date}
                />) : '-'
            ),
            cellwidth: 154

        },
        {
            Header: 'Modified by',
            accessor: (row) => (
                row?.modifiedBy.name ? (<RenderText
                    title={row?.modifiedBy.name}
                    subTitle={row?.modifiedBy.date}
                />) : '-'
            ),
            cellwidth: 154
        },
        {
            Header: 'Action',
            accessor: (row) => (
                <DropdownV2
                    className="iconDropdown"
                    icon='context_menu__l__filled'
                    color="dark"
                    kind='silent'
                    dropdownItems={tableActionItems}
                    onChange={(item) => {
                        setSelectedRowData(row);
                        item.onSelectedItem(row);
                    }}
                />
            ),
            cellwidth: 60
        }
    ];

    const tableActionItems = [
        {
            isDisabled: false,
            label: 'Change Schedule',
            value: 'Change Schedule',
            counter: '',
            onSelectedItem: (row) => {
                const hasConflict = row?.demand?.hasConflict;
                const hasBudget = row?.demand?.status === 'pending'
                const isProjectPage = true;

                if (hasConflict) {
                    onHavingConflictShowAlert(hasConflict, isProjectPage);
                    return;
                }
                if (hasBudget) {
                    onHavingBudgetExceededShowAlert();
                    return;
                }
                setSelectedRowData(row);
                setIsArticle(row.isArticle);
                setShowDialog('change-schedule');
            }
        },
        {
            isDisabled: false,
            label: 'Un-allocate',
            value: 'Un-allocate',
            onSelectedItem: (row) => {
                const currentDate = moment.utc().startOf('day');
                const startDateInUTC = moment.utc(row?.startDate).add(1, 'days');
                if (!isAdminOrRM && !startDateInUTC.isSameOrAfter(currentDate)) {
                    setToastData({ show: true, message: 'Unallocation for past dates can only be done by your Resource Manager. Please request your RM to unallocate ONLY if the practitioner has never worked on this demand. However, if the practitioner has worked for a certain duration, the dates can be modified to reflect accurate schedules.', type: 'error', variant: 'success' });
                    return;
                }

                const hasConflict = row?.demand?.hasConflict;
                const hasBudget = row?.demand?.status === 'pending'
                const isProjectPage = true;

                if (hasConflict) {
                    onHavingConflictShowAlert(hasConflict, isProjectPage);
                    return;
                }
                if (hasBudget) {
                    onHavingBudgetExceededShowAlert();
                    return;
                }
                setSelectedRowData(row);
                setShowDialog('un-allocate');
            }
        }
    ];

    const getPractitionerData = async (offset = 0) => {
        const res = await getAllAllocationsFilter(offset, limit, '', '', moment().format('YYYY-MM-DD'), '', '', '', projectId);
        return { count: res.entity.count, rows: allocationToPractitioner(res.entity.rows) };
    };

    const onPageChange = async (pageNo) => {
        if (pageNo - 1 < total) {
            const data = await getPractitionerData((pageNo - 1) * limit);
            setRows(data.rows);
            setTotal(data.count);
            setPaginationOption({
                currentPage: pageNo,
                pageNumberInSection: limit,
                pageLength: Math.ceil(data.count / limit)
            });
        }
    };

    const pagination = {
        ...paginationOption,
        onPageChange
    };

    const AlertTypes = {
        'allocate-budget-exceeded-alert': {
            title: 'Budget Threshold Exceeded',
            description: <span>The total allocation budget has exceeded the defined P2W budget threshold.<br /><br /> Do you wish to proceed?</span>,
            onSave: async () => {
                const allocationData = {
                    ...formData,
                }
                const isBudgetExceed = true;
                onChangeSchedule({ ...allocationData, isBudgetExceed }, formData.row)
                setAlertModalData('');
            },
            saveLabel: 'Allocate',
            cancelLabel: 'Close'
        },
        'already-soft-blocked': {
            title: 'This employee is already soft-blocked for the same demand',
            description: 'This employee is already soft-blocked for the same demand; no allocation is required. Please reach out to your RM for further queries',
            onSave: () => setAlertModalData(''),
            saveLabel: 'Ok',
            showCancelButton: false
        },
        'already-soft-blocked-v2': {
            title: 'Some employees are already soft-blocked for the same demand',
            description: `One of the employees is already soft-blocked to a same demand where he is allocated to. Please reach out to your RM for allocation or further queries.`,
            onSave: () => setAlertModalData(''),
            saveLabel: 'Ok',
            showCancelButton: false
        }
    };

    const onChangeSchedule = async (data) => {
        setFormData(data);
        try {
            const res = await editTenure(selectedRowData.id, {
                startDate: new Date(data.startDate).toUTCString(),
                endDate: new Date(data.endDate).toUTCString(),
                weeklyHours: parseFloat(data.hoursPerWeek),
                includeSaturday: data.include && data.include[0]?.value,
                includeSunday: data.include && data.include[1]?.value,
                adjustedUtilizations: data?.adjustedUtilizations,
                isContinuityResource: data?.isContinuityResource,
                continuityReason: data?.isContinuityResource ? data?.continuityReason?.value : null,
                continuityReasonComment: data?.isContinuityResource ? data?.continuityReasonComment : '',
                isBudgetExceed: data?.isBudgetExceed || false,
                wbsL4Code: data?.wbsL4Code ? typeof data?.wbsL4Code === 'object' ? data?.wbsL4Code?.value : data?.wbsL4Code : null
            });
            if (res?.status) {
                if (res.entity.message === 'Conflict Error') {
                    setShowConflict({ show: true, noOfError: 'some', data: res?.entity?.data });
                    return;
                }

                if (res.entity.message === 'Expected Budget Exceeding') {
                    setAlertModalData('allocate-budget-exceeded-alert')
                    return;
                }

                fetchData();
                await fetchWalletBalance()
                setShowDialog('');
                setAlertModalData('');
                setShowToast({ visible: true, message: res?.message });
                return { success: true };

            } else if (res.status === false && res?.error?.response?.data?.error === 'Soft-block exist issue') {
                return setAlertModalData('already-soft-blocked')
            } else if (res?.error?.response?.status === 400) {
                // if (res?.error?.response?.data?.error === 'Date Validation Error') {
                //     setToastData({ show: true, message: 'Schedule start date should be on or after the current date.', type: 'error', variant: 'success' });
                //     return
                // }

                setToastData({ show: true, message: res?.error?.response?.data?.message, type: 'error', variant: 'success' });
            }
            else {
                setShowToast({ visible: true, message: 'Failed to unallocate employee from this demand', variant: 'error' });
            }
            return { success: false, validationErrors: {} };
        } catch (err) {
            setShowToast({ visible: true, message: err?.response?.data?.message || 'Something went wrong', variant: 'error' });
        }
    };

    const onSaveConflict = async () => {
        if (showConflict.data) {
            const res = await createConflict({
                ...showConflict.data,
                suggestionId: showConflict.data?.existingSuggestionId,
                softblockId: showConflict.data?.existingSoftBlockId,
                proposalId: showConflict.data?.existingProposalId
            });
            if (res.status) {
                setShowDialog('');
                fetchData();
                setShowConflict({ show: false });
                setShowToast({ visible: true, message: 'Conflict raised successfully. Please go and resolve in conflicts tab' });
            } else {
                setShowConflict({ show: false });
                setShowToast({ visible: true, message: res.error.response?.data?.message ?? 'Error while raising conflict', variant: 'error' });
            }
            setTimeout(() => {
                setShowToast({});
            }, 3000);
        }
    };

    const onUnAllocate = async (data) => {
        try {
            const res = await updateAllocation(selectedRowData.id, {
                endDate: new Date(selectedRowData.allocationEndDate).toUTCString(),
                unallocateReason: data?.unallocateReason
            });
            if (res.status) {
                fetchData();
                await fetchWalletBalance()
                setShowToast({ visible: true, message: res?.message });
            } else {
                throw res.error;
            }
        } catch (err) {
            const extractedMessage = extractApiErrorMessage(err?.response?.data, 'Failed to Unallocate');
            setShowToast({
                visible: true,
                message: extractedMessage,
                variant: 'error'
            });
        } finally {
            setTimeout(() => setShowToast({ visible: false, message: '' }), 2000);
            setShowDialog('');
        }
    };

    const onMultiUnAllocate = async (data, rowArray) => {
        const resourceData = rowArray.map((item) => ({ resourceId: item.resourceId, allocationId: item.id }));
        const updateRes = await bulkUnAllocation(data.unallocateReason, resourceData);

        if (updateRes.status) {
            fetchData();
            await fetchWalletBalance()
            setShowDialog('');
            setSelectMultiRows(() => []);
            setUnAllocateData(null);
            setShowToast({ visible: true, message: updateRes?.message });
            setTimeout(() => {
                setShowToast({});
            }, 2000);
            return { success: true };
        }
        if (updateRes.error?.response?.status === 400) {
            const responseData = updateRes?.error?.response?.data || {};
            if (responseData.error === 'Date Validation Error') {
                setToastData({ show: true, message: 'Unallocation for past dates can only be done by your Resource Manager. Please request your RM to unallocate ONLY if the practitioner has never worked on this demand. However, if the practitioner has worked for a certain duration, the dates can be modified to reflect accurate schedules.', type: 'error', variant: 'success' });
            } else {
                const message = extractApiErrorMessage(responseData, 'Failed to unallocate employee from this demand');
                setShowToast({ visible: true, message, variant: 'error' });
            }
        } else {
            const fallbackMessage = extractApiErrorMessage(updateRes?.error?.response?.data, 'Failed to unallocate employee from this demand');
            setShowToast({
                visible: true,
                message: fallbackMessage,
                variant: 'error'
            });
        }
        setTimeout(() => {
            setShowToast({});
        }, 3000);
        return { success: false, validationErrors: {} };
    };


    const onMultiAllocate = async (data, rowArray) => {
        const existingSoftblockResources = rowArray.filter(item => item.demand?.softBlocks.some(sb => sb.resourceId === item.resourceId)).map(item => item.practitioner?.name).join(', ');
        if(existingSoftblockResources?.length && isManagerOrPartner) { 
            setAlertModalData('already-soft-blocked-v2');
            return;
        }
        const formatDate = (val) => (val && !isNaN(new Date(val)) ? new Date(val).toUTCString() : 'Invalid Date');
        const resourceData = rowArray.map((item) => ({ resourceId: item.resourceId, allocationId: item.id }));
        const updateRes = await updateBulkSchedule({
            startDate: formatDate(data.startDate),
            endDate: formatDate(data.endDate),
            weeklyHours: data.hoursPerWeek,
            ...(data.include?.[0]?.value ? { includeSaturday: true } : {}),
            ...(data.include?.[1]?.value ? { includeSunday: true } : {}),
        }, resourceData);

        if (updateRes.status) {
            fetchData();
            await fetchWalletBalance()
            setShowDialog('');
            setShowToast({ visible: true, message: updateRes?.message });
            setTimeout(() => {
                setShowToast({});
            }, 2000);
            return { success: true };
        }

        if (updateRes.error?.response?.status === 400) {
            if (updateRes.error.response.data.data.error === 'Conflict Error') {
                const errorMsg = updateRes.error.response?.data?.data?.message || [];

                setShowToast(
                    {
                        visible: true,
                        message: `${errorMsg.length || 'Some'} ${errorMsg.length > 1 ? 'resources' : 'resource'} not available for the given schedule`,
                        variant: 'error'
                    }
                );
            }
            //  else if (updateRes.error.response.data.data.error === 'Curent Date Validation Error') {
            //     setToastData({ show: true, message: 'Schedule start date should be on or after the current date.', type: 'error', variant: 'success' });
            // }
            else if (updateRes.error.response.data.data.error === 'Date Validation Error') {
                setShowToast(
                    {
                        visible: true,
                        message: `${updateRes?.error?.response?.data?.data?.message?.length || 'Some'} resources having start date or end date validation error`,
                        variant: 'error'
                    }
                );
            } else if (updateRes.error.response.data.data.error === 'Hours Validation Error') {
                const errorMsg = updateRes.error.response?.data?.data?.message || [];

                setShowToast(
                    {
                        visible: true,
                        message: `For ${errorMsg.length || 'Some'} ${errorMsg.length > 1 ? 'resources' : 'resource'} applied hours is more than capacity`,
                        variant: 'error'
                    }
                );
            }
            else if (updateRes.error.response.data.message.length) {
                const message = updateRes.error.response.data.message
                    .filter(({ error }) => error)
                    .map(({ error }) => error.errorMessage).join(', ');
                setShowToast({ visible: true, message: message, variant: 'error' });
            }
            else if (updateRes.error.response.status === 400) {
                const errorMsg = updateRes.error.response?.data?.data?.message || [];

                const msg = errorMsg.length && errorMsg[0]?.error?.errorMessage || errorMsg;

                setShowToast(
                    {
                        visible: true,
                        message: msg || updateRes?.data?.error || 'Something went wrong',
                        variant: 'error'
                    }
                );
            } else {
                setShowToast({ visible: true, message: updateRes.message, variant: 'error' });
            }
        } else {
            setShowToast({ visible: true, message: 'Failed to unallocate employee from this demand', variant: 'error' });
        }
        return { success: false, validationErrors: {} };
    };

    const downloadCsv = async () => {
        trackAdobeAnalyticEvent({
            reportType: "allocation-project-export",
            filterApplied: Boolean(filterAndSort?.filters)
        })
        let type;
        type = 'Email';
        const res = await getAllocationUnderProjectCSV({ search, filters: getFiltersString(filterAndSort?.filters, true), sort: getSortString(filterAndSort.sort), projectId });

        if (res.status) {
            const { jobId } = res?.entity;
            window.open(`/download-history?jobId=${jobId}`, '_blank');
        } else {
            setShowToast({
                visible: true,
                message: findFirstStringByKey(res.error.response, 'message'),
                variant: 'error'
            });
        }
    };

    const throttleExportOnClick = throttle(downloadCsv);

    const fetchData = async () => {
        setShowLoader(true)
        const data = await getPractitionerData(0);
        setRows(data.rows);
        setTotal(data.count);
        setPaginationOption({
            currentPage: 1,
            pageNumberInSection: limit,
            pageLength: Math.ceil(data.count / limit)
        });
        setShowLoader(false)
    };

    useEffect(() => {
        fetchData();
    }, []);

    const loadOptions = (item, searchValue, data, page) => {
        return FilterService.getFiltersOptionData(item.name.replace('filters.', ''), searchValue, null, null, data, page);
    };

    const onSearch = async (search, filters, sort) => {        
        const fil = getFiltersString(filters, true);        
        const sor = getSortString(sort);
        const allocationd = await getAllAllocationsFilter(0, limit, '', '', moment().startOf('day'), search, fil, sor, projectId);
        setRows(allocationToPractitioner(allocationd?.entity?.rows));
        setTotal(allocationd?.entity?.count);
        setPaginationOption({
            currentPage: 1,
            pageNumberInSection: limit,
            pageLength: Math.ceil(allocationd?.entity?.count / limit)
        });
    };

    const onFilterChange = (data) => {
        const search = getSearchString(data.filterSearchFor, data.filterSearch);
        setSearch(() => search);
        onSearch(search, data.filters, data.sort);
        setFilterAndSort({ ...filterAndSort, filters: data.filters, sort: data.sort });
    };    

    const FilterProps = {
        title: `${total} data found`,
        view: 'Allocations',
        actionItems: [],
        filterItems: projectDetailAllocationTabFiltersItems.map((item) => ({
            ...item,
            loadOptions: (input, data, page) => loadOptions(item, input, data, page)
        })),
        onFilterChange,
        sortItems: projectDetailAllocationSortItems,
        searchFilterItems: projectDetailSearchFilterItems,
        FilterService,
        session,
        isExport: true,
        isExportAlert: total > 0 ? true : false,
        customMessage,
        onExport: total > 0 ? throttleExportOnClick : () => setShowToast({
            visible: true,
            message: 'No data available to export',
            variant: 'error'
        }),
    };


    const setMultiRows = (data) => {
        setSelectMultiRows([...selectMultiRows, data]);
    };
    const removeMultiRows = (data) => {
        setSelectMultiRows(selectMultiRows.filter((row) => row.id !== data.id));
    };
    const selectAllRows = () => {
        setSelectMultiRows(() => rows.filter(row => !row.isSelectDisabled));
    };
    const removeAllRows = () => {
        setSelectMultiRows(() => []);
    };

    const unallocate = (props) => {
        const currentDate = moment.utc().startOf('day');
        let showToast = false;
        for (const row of selectMultiRows) {
            const startDateInUTC = moment.utc(row?.allocationStartDate).add(1, 'days');
            if (!isAdminOrRM && !startDateInUTC.isSameOrAfter(currentDate)) {
                showToast = true;
                break;
            }
        }

        if (showToast) {
            setToastData({
                show: true,
                message: 'Un-allocation for the past dates can only be done by your Resource Manager, please request your RM to un-allocate ONLY if the practitioner has never worked on this demand at all. However, if the practitioner did work for a certain duration, then the dates could be modified to reflect accurate schedules.',
                type: 'error',
                variant: 'success'
            });
        } else {
            setShowDialog('multi-un-allocate');
        }
    }

    const changeschedule = (props) => {
        setShowDialog('multi-change-schedule')

    }    

    return (
        <>
            <ActionsWithFilter
                disableSave
                isShowFilter
                titleType='h6'
                isFav={false}
                {...FilterProps}
            />
            <Table
                columns={columns}
                tableData={rows}
                isFronzen
                pagination={pagination}
                isDefaultTableCard
                isSelection={true}
                className='md:max-h-[calc(100vh-13rem)] flex flex-col'
                isStickyHeader
                selectMultiRows={setMultiRows}
                removeMultiRows={removeMultiRows}
                selectedRows={selectMultiRows}
                selectAllRows={selectAllRows}
                removeAllRows={removeAllRows}
                size='s'
            />
            {showDialog === 'change-schedule' && <Schedule
                show={showDialog === 'change-schedule'}
                onClose={() => setShowDialog('')}
                onSave={onChangeSchedule}
                defaultValues={{
                    startDate: moment(selectedRowData?.allocationStartDate).format('MM/DD/YYYY'),
                    endDate: moment(selectedRowData?.allocationEndDate).format('MM/DD/YYYY'),
                    hoursPerWeek: selectedRowData?.hoursPerWeek,
                    include: selectedRowData?.include || [],
                    allocationAdjustedUtilizations: selectedRowData?.adjustedUtilizations,
                    isContinuityResource: selectedRowData?.isContinuityResource,
                    ...(selectedRowData?.continuityReason
                        ? {
                            continuityReason: {
                                label: selectedRowData.continuityReason,
                                value: selectedRowData.continuityReason
                            }
                        }
                        : {}),
                    continuityReasonComment: selectedRowData?.continuityReasonComment
                }}
                isEdit={true}
                isAllocation={true}
                isArticle={isArticle}
                isAudit={selectedRowData?.isAudit}
                demandData={selectedRowData?.demand}
                wbsDropdownData={{
                    demandData: {
                        swiftWbsCode: {
                            billable_l4_wbs_code: selectedRowData?.demand?.swiftWbsCode?.billable_l4_wbs_code
                        },
                        sapProject: {
                            engagementCode: selectedRowData?.demand?.sapProject?.engagementCode
                        }
                    },
                    projectDetails: {
                        linkedWbsL4Codes: selectedRowData?.demand?.project?.linkedWbsL4Codes,
                        linkedWbsL4Count: selectedRowData?.demand?.project?.linkedWbsL4Count,
                        id: selectedRowData?.demand?.project?.id,
                        opportunityId: selectedRowData?.demand?.project?.clientOpportunity?.opportunityId
                    }
                }}
            />}
            <MultiChangeSchedule
                show={showDialog === 'multi-change-schedule'}
                onClose={() => setShowDialog('')}
                onSave={(data) => onMultiAllocate(data, selectMultiRows)}
            />
            {
                showConflict?.show && (
                    <EmployeeScheduleConflict
                        onClose={() => setShowConflict({ show: false })}
                        onSave={onSaveConflict}
                    />
                )
            }
            <AlertModal
                title={AlertTypes[alertModalData]?.title}
                show={alertModalData === 'allocate-budget-exceeded-alert' || alertModalData === 'already-soft-blocked' || alertModalData === 'already-soft-blocked-v2'}
                saveLabel={AlertTypes[alertModalData]?.saveLabel}
                cancelLabel={AlertTypes[alertModalData]?.cancelLabel}
                description={AlertTypes[alertModalData]?.description}
                onClose={() => setAlertModalData('')}
                onSave={AlertTypes[alertModalData]?.onSave}
                showCalcelButton={!(alertModalData === 'already-soft-blocked' || alertModalData === 'already-soft-blocked-v2')}
            />
            <UnAllocate
                show={showDialog === 'multi-un-allocate'}
                onClose={() => {
                    setShowDialog('');
                    setUnAllocateData(null);
                }}
                onSave={(data) => {
                    setUnAllocateData(data);
                    setShowDialog('multi-un-allocate-alert');
                }}
                isMulti={true}
            />
            <UnAllocate
                show={showDialog === 'un-allocate'}
                onClose={() => setShowDialog('')}
                onSave={onUnAllocate}
            />
            <Toast
                show={showToast.visible}
                message={showToast.message}
                variant={showToast.variant}
                onClose={() => setShowToast({ visible: false, message: '' })}
            />
            {
                selectMultiRows.length > 1 && (
                    <div className='flex justify-between md:sticky bottom-0 md:px-6 md:py-8 z-[100] py-6 mt-4 border-t solid bg-white items-center'>
                        <Heading type='h6' className=''>{selectMultiRows.length} allocation selected</Heading>
                        <div className='flex gap-3'>
                            <Button
                                label="Un-allocate"
                                Size="m"
                                kind="secondaryLoud"
                                color='dark' onClick={unallocate}
                                isDisabled={selectMultiRows.length === 0}
                            />
                            <Button
                                label="Change Schedule"
                                Size="m" color="green"
                                onClick={changeschedule}
                                isDisabled={selectMultiRows.length === 0}
                            />
                        </div>
                    </div>
                )
            }
            <AlertModal
                show={showDialog === 'multi-un-allocate-alert'}
                title='Unallocate Employee'
                description='You are about to unallocate this employee and delete the schedule from this project. Do you wish to proceed?'
                saveLabel='Yes'
                cancelLabel='No'
                onSave={() => {
                    onMultiUnAllocate(unAllocateData, selectMultiRows);
                }}
                onClose={() => {
                    setShowDialog('');
                    setUnAllocateData(null);
                }}
                onCancel={() => {
                    setShowDialog('');
                    setUnAllocateData(null);
                }} />
            {showLoader && <Loader />}
        </>
    );
};

export default PractitionerAllocatedView;
